import {useForm} from "react-hook-form";
import {useParams} from "react-router-dom";
import useUpdateProjects from "./useUpdateProjects";
import {yupResolver} from "@hookform/resolvers/yup";
import {ERR_MESSAGES} from "../../../utils/constants";
import {useCallback, useEffect, useState} from "react";
import {getFormDataObj} from "../../../components/ReactHookForm/utils";
import {sortArraysAndReturnNotEqualItems} from "../../../utils/arrays";
import {projectFormSchema} from "../../../components/ReactHookForm/schemes";
import {
  CancelToken,
  deleteProjectRequest,
  getGroupsRequest,
  getMembersRequest,
  getProjectDataRequest,
  getRolesRequest,
  groupCancelSource,
  patchProjectDataRequest,
  updateRolesRequest
} from "../../../services/api";

const useProjectInfo = () => {
  const {id} = useParams();
  const [projectInfo, setProjectInfo] = useState({header: "", isMainProject: false});
  const [{roles, serverGroupsAndMembers}, setProjectRoles] = useState({
    roles: {members: [], groups: []},
    serverGroupsAndMembers: {members: [], groups: []},
  });
  const [availableGroupsAndMembers, setAvailableGroupsAndMembers] = useState({members: [], groups: []});
  const updateProjects = useUpdateProjects();

  const formControl = useForm({
    mode: "onChange",
    resolver: yupResolver(projectFormSchema),
    defaultValues: {name: ""}
  });

  const {reset} = formControl;

  const updateProjectData = (projectData) => {
    return patchProjectDataRequest(id, projectData)
      .then(updateProjects);
  };

  const deleteProject = () => {
    return deleteProjectRequest(id)
      .then(updateProjects);
  };

  const getProjectData = useCallback(() => {
    return Promise.all([getRolesRequest(id), getProjectDataRequest(id), getMembersRequest(), getGroupsRequest()]).then(
      ([rolesResponse, projectDataResponse, membersResponse, groupsResponse]) => {
        setProjectRoles((oldState) => ({
          ...oldState,
          roles: {members: rolesResponse.data.members, groups: rolesResponse.data.groups},
          serverGroupsAndMembers: {
            members: membersResponse.data.filter((member) => member.is_superuser !== true),
            groups: groupsResponse.data
          },

        }));
        setProjectInfo({
          header: projectDataResponse.data.name,
          isMainProject: projectDataResponse.data.is_main
        });

        const formData = getFormDataObj({arrOfFields: ["name"], serverData: projectDataResponse.data});
        reset(formData);
      }
    );
  }, [id, reset]);

  const getRoles = () => {
    return getRolesRequest(id)
      .then((response) => response.data);
  };

  useEffect(() => {
    if (Object.keys(roles).length) {
      setAvailableGroupsAndMembers({
        members: sortArraysAndReturnNotEqualItems(
          serverGroupsAndMembers.members,
          roles.members.map((item) => item.member)
        ),
        groups: sortArraysAndReturnNotEqualItems(
          serverGroupsAndMembers.groups,
          roles.groups.map((item) => item.group)
        )
      });
    }
  }, [serverGroupsAndMembers, roles]);

  useEffect(() => {
    if (id) {
      groupCancelSource.value = CancelToken.source();
      getProjectData();
    }

    return () => {
      groupCancelSource.value?.cancel(ERR_MESSAGES.CANCELLED_REQUEST);
      groupCancelSource.value = null;
    };
  }, [id, getProjectData]);

  const updateRolesWithApi = (newRoles) => {
    return updateRolesRequest(newRoles, id)
      .then(() => getRoles())
      .then((roles) => setProjectRoles((oldState) => ({
        ...oldState,
        roles
      })));
  };

  const addRoles = (newItemsPluralName, newRoles) => {
    return updateRolesWithApi({
      ...roles,
      [newItemsPluralName]: [...roles[newItemsPluralName], ...newRoles]
    }, id);
  };

  const updateRoles = (newItemsPluralName, newRoles) => {
    return updateRolesWithApi({
      ...roles,
      [newItemsPluralName]: newRoles
    });
  };

  return {
    roles,
    addRoles,
    updateRoles,
    formControl,
    projectInfo,
    deleteProject,
    updateProjectData,
    availableGroupsAndMembers,
  };
};

export default useProjectInfo;